import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { DistributionObjectGroup } from '../models/document-type-group';
import { DeveloperService } from '../services/developer.service';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoColumnFixingModule, DxoEditingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-developer-distribution-object-groups',
    templateUrl: './developer-distribution-object-groups.component.html',
    styleUrls: ['./developer-distribution-object-groups.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoColumnFixingModule, DxoEditingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule]
})
export class DeveloperDistributionObjectGroupsComponent implements OnInit, AfterViewInit {
    documentTypeGroups: DistributionObjectGroup[] = [];
    allowAdding = false;
    allowUpdating = false;
    allowDeleting = false;
    allowShowLog = false;

    constructor(
        private developerService: DeveloperService,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) { }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Developer_Action_DocumentTypeGroup_Create');
        this.allowUpdating = this.currentUserService.hasPermission('Developer_Action_DocumentTypeGroup_Edit');
        this.allowDeleting = this.currentUserService.hasPermission('Developer_Action_DocumentTypeGroup_Delete');
        this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_ShowLog');
    }

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.documentTypeGroups = [];
            this.documentTypeGroups = await this.developerService.getDistributionObjectGroups();
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onInitNewRow(e: any): void {
        e.data = { DistributionObjectGroupId: null, Name: '', Description: '', Color: '' } as DistributionObjectGroup;
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as DistributionObjectGroup, item => this.saveDocumentTypeGroup(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as DistributionObjectGroup, item => this.saveDocumentTypeGroup(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as DistributionObjectGroup, item => this.removeDocumentTypeGroup(item));
    };

    async saveDocumentTypeGroup(documentTypeGroup: DistributionObjectGroup): Promise<DistributionObjectGroup> {
        const documentTypeGroupExist = this.documentTypeGroups.find(i => i.Name === documentTypeGroup.Name && i.DistributionObjectGroupId !== documentTypeGroup.DistributionObjectGroupId);
        if (documentTypeGroupExist != null) {
            throw new Error('Another document type group with that name already exists.');
        }

        const newItem = await this.developerService.saveDistributionObjectGroup(documentTypeGroup);
        this.toastService.showToast('Distribution object group saved...', ToastType.success);
        return newItem;
    }

    async removeDocumentTypeGroup(documentTypeGroup: DistributionObjectGroup): Promise<DistributionObjectGroup> {
        await this.developerService.deleteDistributionObjectGroup(documentTypeGroup.DistributionObjectGroupId);
        this.toastService.showToast('Document type group deleted...', ToastType.success);
        return documentTypeGroup;
    }
}
